import {
    isEmpty,
    isNil,
    sortBy,
    uniq,
    difference,
    isEqual,
    cloneDeep,
} from 'lodash-es'

import {
    DiscountAppliesTo,
    DiscountContract,
    DiscountLevel,
    DiscountType,
    KitchenPrintStatus,
    OrderFulfillmentStatus,
    OrderLineKdsStatus,
    OrderLineStatus,
    OrderPaymentStatus,
    OrderStatus,
    VoidCompReasonModel,
    VoidCompReasonType,
} from 'models'
import {
    DiscountContractWithCustom,
    DiscountContractWithItemId,
    emptyDeliveryAddress,
    IOrderServiceChargeSetting,
    OrderFulfilmentType,
    OrderItemViewModel,
    OrderProcessingMode,
    SelectItemInCartType,
    SellProcessingState,
    SellState,
} from 'store/models/sellModels'
import {
    autoDiscountByEntireOrderMapping,
    autoDiscountByItemsMapping,
    getEntireDiscountInApplicables,
} from 'views/Sale/discount/discountMapper/discountMapper'
import { sortDiscountTypeOfDiscount } from 'views/Sale/discount/discountMapper/discountSortting'
import { OrderChannel } from 'constant/OrderChannels'
import toast from 'utilities/toast/toast'
import { selectDefaultMenuSection } from 'views/Sale/utils/POSData/selectDefaultMenuSection'
import { ISellActionType } from './ISellActionType'
import dayjs from 'dayjs'
import processOrderSale from 'views/Sale/utils/processOrderSale'
import {
    FUTURE_DISCOUNT_TYPE,
    SIMPLE_DISCOUNT_TYPE,
} from 'views/Discounts/hooks/useUpsertDiscountSchema'
import {
    BuyXGetYResult,
    isPromotionItem,
    mapRequiredItemWithDiscount,
} from 'components/Sell/DiscountBogoDialog/hook/buyXgetYProcess'
import {
    buildEntireOrderDiscounts,
    buildEntireVoidCompModel,
} from 'components/Sell/OrderLookup/OrderInfo/buildOrderToOrderViewModel'
import { LineModelExtend } from 'views/Sale/utils/orderCalculation/models/IOrderCalculationModel'
import { mergeCartItems } from 'views/Sale/QuickService/contexts/QuickServiceSale.shared'
import {
    isSameOrderItem,
    isUseSamePriceList,
} from 'components/Sell/utils/common'
import RecalculateOrder from 'views/Sale/QuickService/hooks/RecalculateOrder'
import {
    ALL_ITEMS_CATEGORY_ID,
    MENUSECTION_ALL_ITEMS,
} from 'views/Sale/utils/POSData/defaultPOSData'
import { SelectedOrderItem } from 'components/Sell/VoidDialog/contexts/VoidCompDialogContext'

const defaultServiceChargeSetting = (
    defaultServiceChargeRate?: number,
): IOrderServiceChargeSetting => ({
    disableServiceCharge: false,
    currentServiceChageRate: defaultServiceChargeRate ?? 0,
    useManualServiceCharge: false,
})

export const initialState: SellState = {
    isCheckingOrderStockAvailability: false,
    orderChannelID: OrderChannel.QuickService,
    selectedMenu: undefined,
    selectOnlineStoreId: '',
    selectedCategoryId: ALL_ITEMS_CATEGORY_ID,
    selectedMenuSectionId: MENUSECTION_ALL_ITEMS,

    items: [],

    orderItems: [],
    latestOrderItem: undefined,
    orderNote: '',
    taxResult: { taxLines: [] },
    entireOrderDiscounts: [],
    sellProcessingState: SellProcessingState.Idle,
    sellProcessingMessage: '',
    sellProcessingAdditionValue: undefined,
    orderProcessingMode: OrderProcessingMode.NON_SELECTED,

    isCustomerDialogOpen: false,
    currentFulfilmentType: OrderFulfilmentType.DINE_IN,
    fulfilmentLoading: false,
    activeFulfillment: [OrderFulfilmentType.DINE_IN],

    orderServiceChargeSetting: defaultServiceChargeSetting(),
    defaultServiceChargeRate: 0,

    selectedItemInCart: undefined,
    excludeAutoDiscountIds: [],
    orderCustomerID: undefined,
    selectedBrandID: '',
    searchString: '',
    previousSelected: {
        categoryId: ALL_ITEMS_CATEGORY_ID,
        menuSectionId: MENUSECTION_ALL_ITEMS,
    },
    isReceiptDialogOpen: false,

    isVoidDialogOpen: false,

    // Gift card global state
    isGiftCardDialogOpen: false,
    openAlertDiscountInApplicable: false,
    removedData: {},
    savedOrderItems: [],
    pickupDateTime: null,
    isAsap: null,
    deliveryAddress: emptyDeliveryAddress(),

    loadOrderState: SellProcessingState.Idle,
    hasAwaitingProcessTransaction: false,

    waitingSentToKdsOrderLineIds: [],
}

export default function saleReducer(
    state: SellState = initialState,
    action: ISellActionType,
): SellState {
    switch (action.type) {
        case 'SELECT_ORDER_CART_ITEM': {
            return {
                ...state,
                selectedItemInCart: action.payload,
            }
        }
        case 'SET_RECEIPT_LOOKUP_DIALOG_OPEN_STATUS': {
            return {
                ...state,
                isReceiptDialogOpen: action.payload,
            }
        }
        case 'SELL_SELECT_BRAND_ID': {
            return {
                ...state,
                selectedBrandID: action.payload,
            }
        }

        case 'RE_CALCULATE_LINE_TOTAL': {
            const {
                orderView,
                priceList,
                comboChoicesMap,
                entityModifiersMap,
                comboModifierOptions,
            } = action.payload
            const { orderItems } = state
            const newOrderItems = cloneDeep(orderItems).map((oItem) => {
                if (oItem.id === orderView.id) {
                    const recalculateOrder = new RecalculateOrder(
                        oItem,
                        orderView.menuId!,
                        priceList,
                        comboChoicesMap,
                        entityModifiersMap,
                        comboModifierOptions,
                    )
                    recalculateOrder.process()
                    return recalculateOrder.getOrderItemView()
                }
                oItem.isSaved = false
                return {
                    ...oItem,
                }
            })

            return {
                ...state,
                orderItems: newOrderItems,
            }
        }
        case 'SELL_SELECT_MENU': {
            const selectedMenu = action.payload
            const menuItems = selectedMenu?.items ?? []
            return {
                ...state,
                selectedMenu,
                selectedMenuSectionId: selectDefaultMenuSection(
                    selectedMenu,
                    state.selectedBrandID,
                    menuItems,
                ),
                selectedCategoryId: ALL_ITEMS_CATEGORY_ID,
                items: menuItems,
                selectOnlineStoreId: selectedMenu?.isOnlineMenu
                    ? state.selectOnlineStoreId
                    : undefined,
            }
        }
        case 'SELL_SELECT_ONLINE_STORE': {
            const { onlineStoreID, selectedMenu } = action.payload
            const menuItems = selectedMenu?.items ?? []

            return {
                ...state,
                selectedMenu,
                selectedMenuSectionId: selectDefaultMenuSection(
                    selectedMenu!,
                    state.selectedBrandID,
                    menuItems,
                ),

                selectedCategoryId: ALL_ITEMS_CATEGORY_ID,
                items: menuItems,
                selectOnlineStoreId: onlineStoreID,
            }
        }
        case 'SELL_SELECT_MENUSECTION': {
            return {
                ...state,
                selectedMenuSectionId: action.payload,
                selectedCategoryId: ALL_ITEMS_CATEGORY_ID,
            }
        }
        case 'SELL_SELECT_CATEGORY': {
            return {
                ...state,
                selectedMenuSectionId: MENUSECTION_ALL_ITEMS,
                selectedCategoryId: action.payload,
            }
        }
        case 'SELL_FINALIZE_CALCULATION': {
            const newEntireOrderDiscounts = state.entireOrderDiscounts.some(
                (s) => !s.isAutomaticDiscount,
            )
                ? state.entireOrderDiscounts
                : prepareEntireOrderDiscounts(
                      state.orderItems.filter(
                          (oi) => !Boolean(oi.item.baseDepositAndCreditItemId),
                      ),
                      action.payload.discounts,
                      state.currentFulfilmentType,
                      state.excludeAutoDiscountIds,
                      state.orderChannelID,
                  )

            const { processedOrderItems, orderCalculated: calculateResult } =
                processOrderSale(
                    state.orderItems,
                    {
                        currentServiceChageRate:
                            state.orderServiceChargeSetting
                                .currentServiceChageRate,
                        currentFulfilmentType: state.currentFulfilmentType,
                        entireOrderDiscounts: newEntireOrderDiscounts,
                        entireOrderVoidComp: state.entireOrderVoidComp,
                    },
                    action.payload,
                )
            if (
                state.editingOrder?.orderPaymentStatus ===
                OrderPaymentStatus.PartiallyPaid
            ) {
                const sumPaid = state.orderSale?.balanceDueAmount ?? 0
                calculateResult.remainingAmount =
                    (calculateResult.totalDue ?? 0) - sumPaid
            }

            // because `processOrderSale` function mutate the element in `state.orderItems` into `calculatedOrderItems`
            // this might cause `selectedItemInCart.data` pointed to dangling memory if selected item type is 'item'.
            // So, we have to update the pointer
            let newSelectedItemInCart = state.selectedItemInCart
            if (
                newSelectedItemInCart &&
                newSelectedItemInCart.type === SelectItemInCartType.item
            ) {
                const newSelectedItem = processedOrderItems.find(
                    (i) => i.id === state.selectedItemInCart?.data.id,
                )
                if (newSelectedItem)
                    newSelectedItemInCart.data = newSelectedItem
                else newSelectedItemInCart = undefined
            }

            return {
                ...state,
                orderItems: processedOrderItems,
                selectedItemInCart: newSelectedItemInCart,
                taxResult: calculateResult,
                entireOrderDiscounts: newEntireOrderDiscounts,
            }
        }
        case 'SELL_SET_ORDER_SERVICE_CHARGE': {
            const rate = action.payload
            return {
                ...state,
                orderServiceChargeSetting: {
                    disableServiceCharge: false,
                    currentServiceChageRate: rate,
                    useManualServiceCharge: true,
                },
            }
        }
        case 'SELL_REMOVE_ORDER_SERVICE_CHARGE': {
            return {
                ...state,
                orderServiceChargeSetting: defaultServiceChargeSetting(0),
            }
        }
        case 'SELL_RESET_ORDER_SERVICE_CHARGE': {
            return {
                ...state,
                orderServiceChargeSetting: defaultServiceChargeSetting(
                    state.defaultServiceChargeRate,
                ),
            }
        }
        case 'REPLACE_CARTS': {
            return { ...state, orderItems: action.payload }
        }
        case 'SELL_ADD_ITEM': {
            const { newItems, menus, priceList, autoItemDiscounts } =
                action.payload
            const {
                orderItems,
                currentFulfilmentType,
                orderChannelID,
                excludeAutoDiscountIds,
                waitingSentToKdsOrderLineIds,
            } = state

            let orderItemsAppended: OrderItemViewModel[] = orderItems
            let latestOrderItem: OrderItemViewModel = newItems[0]

            newItems.forEach((l) => {
                const { newOrderItems, newAddedItem } = mergeCartItems(
                    orderItemsAppended,
                    l,
                    waitingSentToKdsOrderLineIds,
                    menus,
                    priceList,
                )
                orderItemsAppended = [...newOrderItems]
                latestOrderItem = newAddedItem
            })

            const itemsWithDiscounts = autoDiscountByItemsMapping({
                automaticDiscounts: autoItemDiscounts,
                checkingFulfillmentType: currentFulfilmentType,
                excludeAutoDiscountIds,
                orderChannel: orderChannelID,
            })(orderItemsAppended)

            const last = itemsWithDiscounts.find(
                (i) => i.id === latestOrderItem.id,
            )

            return {
                ...state,
                latestOrderItem: last,
                selectedItemInCart: last && {
                    type: SelectItemInCartType.item,
                    data: last,
                },
                orderItems: itemsWithDiscounts,
            }
        }
        case 'SELL_MODIFY_ITEM': {
            const {
                currentItem,
                newItem,
                menus,
                priceList,
                autoItemDiscounts,
            } = action.payload
            const {
                orderItems,
                currentFulfilmentType,
                orderChannelID,
                excludeAutoDiscountIds,
            } = state
            let newOrderItems = [...orderItems]

            const existOrderItem = newOrderItems
                .filter((i) => i.id !== newItem.id)
                .find(
                    (i) =>
                        isSameOrderItem(i, newItem) &&
                        isUseSamePriceList(menus, priceList, i, newItem),
                )

            if (existOrderItem) {
                existOrderItem.amount += newItem.amount
                newOrderItems = newOrderItems.filter(
                    (i) => !isEqual(i, currentItem),
                )
            } else {
                newOrderItems = newOrderItems.map((e) => {
                    if (isEqual(e, currentItem)) {
                        return newItem
                    }
                    return e
                })
            }
            const itemsWithDiscounts = autoDiscountByItemsMapping({
                automaticDiscounts: autoItemDiscounts,
                checkingFulfillmentType: currentFulfilmentType,
                excludeAutoDiscountIds,
                orderChannel: orderChannelID,
            })(newOrderItems)

            const last = existOrderItem ?? newItem

            return {
                ...state,
                latestOrderItem: last,
                selectedItemInCart: last && {
                    type: SelectItemInCartType.item,
                    data: last,
                },
                orderItems: itemsWithDiscounts,
            }
        }
        case 'SELL_INCREASE_ORDER_ITEM': {
            const { id, autoItemDiscounts } = action.payload
            const {
                orderItems,
                currentFulfilmentType,
                orderChannelID,
                excludeAutoDiscountIds,
            } = state
            const targetItem = orderItems.find((i) => i.id === id)
            if (!targetItem) return state
            const newOrderItems = orderItems.map((i) =>
                i.id === id ? { ...i, amount: i.amount + 1 } : i,
            )
            const itemsWithDiscounts = autoDiscountByItemsMapping({
                automaticDiscounts: autoItemDiscounts,
                checkingFulfillmentType: currentFulfilmentType,
                excludeAutoDiscountIds,
                orderChannel: orderChannelID,
            })(newOrderItems)
            const updatedItem = itemsWithDiscounts.find((i) => i.id === id)
            return {
                ...state,
                orderItems: itemsWithDiscounts,
                latestOrderItem: updatedItem,
                selectedItemInCart: updatedItem && {
                    type: SelectItemInCartType.item,
                    data: updatedItem,
                },
            }
        }
        case 'SELL_DECREASE_ORDER_ITEM': {
            const { id, autoItemDiscounts } = action.payload
            const {
                orderItems,
                currentFulfilmentType,
                orderChannelID,
                excludeAutoDiscountIds,
            } = state
            const targetItem = orderItems.find((i) => i.id === id)
            if (!targetItem) return state
            const newQuantity = targetItem.amount - 1
            if (newQuantity < 1) {
                const buyXgetYDiscounts =
                    targetItem.discounts
                        ?.filter((d) => d.type === DiscountType.BuyXGetY)
                        .map((d) => d.discountID!) ?? []
                const newExcludeIds = uniq([
                    ...excludeAutoDiscountIds,
                    ...buyXgetYDiscounts,
                ])
                const removeIndex = orderItems.findIndex((i) => i.id === id)
                const newSelectedItem =
                    removeIndex < orderItems.length - 1
                        ? {
                              type: SelectItemInCartType.item,
                              data: orderItems[removeIndex + 1],
                          }
                        : undefined
                return {
                    ...state,
                    orderItems: orderItems.filter((i) => i.id !== id),
                    latestOrderItem: undefined,
                    selectedItemInCart: newSelectedItem,
                    excludeAutoDiscountIds: newExcludeIds,
                }
            }
            const newOrderItems = orderItems.map((i) =>
                i.id === id ? { ...i, amount: newQuantity } : i,
            )
            const itemsWithDiscounts = autoDiscountByItemsMapping({
                automaticDiscounts: autoItemDiscounts,
                checkingFulfillmentType: currentFulfilmentType,
                excludeAutoDiscountIds,
                orderChannel: orderChannelID,
            })(newOrderItems)
            const updatedItem = itemsWithDiscounts.find((i) => i.id === id)
            return {
                ...state,
                orderItems: itemsWithDiscounts,
                latestOrderItem: updatedItem,
                selectedItemInCart: updatedItem && {
                    type: SelectItemInCartType.item,
                    data: updatedItem,
                },
            }
        }
        case 'SELL_ADD_BOGO_ITEM': {
            const { updateItems, menus, priceList } = action.payload

            //filter update item out
            let itemsToBeUpdated = state.orderItems.filter((o) => {
                return !updateItems.some((u) => u.id === o.id)
            })
            sortBy(updateItems, (i) => i.discounts?.length ?? 0).forEach(
                (i) => {
                    //add update item to new orders
                    const { newOrderItems } = mergeCartItems(
                        itemsToBeUpdated,
                        i,
                        state.waitingSentToKdsOrderLineIds,
                        menus,
                        priceList,
                    )
                    itemsToBeUpdated = newOrderItems
                },
            )
            return {
                ...state,
                orderItems: [...itemsToBeUpdated.filter((a) => a.amount > 0)],
            }
        }
        case 'SELL_REMOVE_ITEM': {
            const removeData = action.payload
            const targetItem = removeData.data
            let excludeAutoDiscountIds = state.excludeAutoDiscountIds
            /**
             * remove order cart item and promotion item
             */
            if (removeData.type === SelectItemInCartType.item) {
                const buyXgetYDiscounts =
                    (targetItem as OrderItemViewModel).discounts
                        ?.filter((d) => d.type === DiscountType.BuyXGetY)
                        .map((d) => d.discountID!) ?? []
                excludeAutoDiscountIds = uniq([
                    ...excludeAutoDiscountIds,
                    ...buyXgetYDiscounts,
                ])

                const removeIndex = state.orderItems.findIndex(
                    (i) => i === targetItem,
                )

                // edge case: not found remove item, do nothing
                if (removeIndex < 0) return state

                const newSelectedItem =
                    removeIndex < state.orderItems.length - 1
                        ? {
                              type: SelectItemInCartType.item,
                              data: state.orderItems[removeIndex + 1],
                          }
                        : undefined

                return {
                    ...state,
                    orderItems: state.orderItems.filter(
                        (_, idx) => idx !== removeIndex,
                    ),
                    latestOrderItem: undefined,
                    selectedItemInCart: newSelectedItem,
                    excludeAutoDiscountIds,
                }
            }

            /**
             * remove discount only
             */
            const targetDiscountItem = targetItem as DiscountContractWithItemId
            if (targetDiscountItem.isAutomaticDiscount)
                excludeAutoDiscountIds = [
                    ...excludeAutoDiscountIds,
                    targetDiscountItem.discountID!!,
                ]

            /**
             * entire discounts
             */
            if (targetDiscountItem.level === DiscountLevel.EntireOrder) {
                return {
                    ...state,
                    selectedItemInCart: undefined,
                    latestOrderItem: undefined,
                    excludeAutoDiscountIds,
                    /**
                     * Life hack :p
                     * trigger order items to re-render /re-calculate total
                     */
                    orderItems: state.orderItems.map((m) => m),
                    entireOrderDiscounts: state.entireOrderDiscounts.filter(
                        (f) => f.discountID !== targetDiscountItem.discountID,
                    ),
                }
            }

            /**
             * item discounts
             */
            return {
                ...state,
                excludeAutoDiscountIds,
                orderItems: state.orderItems.map((m) => {
                    if (m.id === targetDiscountItem.id) {
                        return {
                            ...m,
                            discounts: m.discounts?.filter(
                                (f) =>
                                    f.discountID !==
                                    targetDiscountItem.discountID,
                            ),
                            isAutoAppliedDiscount: false,
                        }
                    }
                    return m
                }),
                selectedItemInCart: undefined,
                latestOrderItem: undefined,
            }
        }
        case 'SELL_UPDATE_PICKUP_INFO': {
            const form = action.payload
            const newState: SellState = {
                ...state,
                isAsap: form.isAsap,
                orderCustomerID: form.customerId,
            }
            if (form.isAsap) {
                newState.pickupDateTime = null
            } else {
                const { hours, minutes } = form.pickupTime.toObject()
                const mergeDateTime = form.pickupDate
                    .hour(hours)
                    .minute(minutes)
                    .second(0)
                    .millisecond(0)
                newState.pickupDateTime = mergeDateTime.format()
            }
            return newState
        }
        case 'SELL_RESET_PICKUP_INFO': {
            return {
                ...state,
                isAsap: null,
                pickupDateTime: null,
            }
        }
        case 'SELL_UPDATE_DELIVERY_INFO': {
            const form = action.payload
            const newState: SellState = {
                ...state,
                isAsap: form.isAsap,
                orderCustomerID: form.customerId,
                deliveryAddress: form.deliveryAddress,
            }
            if (form.isAsap) {
                newState.pickupDateTime = null
            } else {
                const { hours, minutes } = form.pickupTime.toObject()
                const mergeDateTime = form.pickupDate
                    .hour(hours)
                    .minute(minutes)
                    .second(0)
                    .millisecond(0)
                newState.pickupDateTime = mergeDateTime.format()
            }
            return newState
        }
        case 'SELL_RESET_DELIVERY_INFO': {
            return {
                ...state,
                isAsap: null,
                pickupDateTime: null,
                deliveryAddress: emptyDeliveryAddress(),
            }
        }
        case 'SELL_ADD_NOTE': {
            return {
                ...state,
                orderNote: action.payload,
            }
        }
        case 'SELL_PROCESS_ORDER_BEGIN': {
            return {
                ...state,
                sellProcessingState: SellProcessingState.Processing,
                loadOrderState: SellProcessingState.Idle, //Set loadOrderState to idel when start to process order.
            }
        }
        case 'SELL_PROCESS_ORDER_SUCCESS': {
            const { order, isOffline } = action.payload
            const lastUpdatedOrderLines = order.orderLines
            if (action.message) toast.success(action.message)
            return {
                ...state,
                billPreviewOrderID: isOffline ? order.orderID : undefined,
                sellProcessingState: SellProcessingState.Success,
                sellProcessingMessage: 'Placed Order Successfully',
                editingOrder: order,
                orderServiceChargeSetting: {
                    ...state.orderServiceChargeSetting,
                    currentServiceChageRate: order.serviceChargeRate ?? 0,
                },
                orderItems: state.orderItems.map((o) => {
                    const newOrderItem = { ...o, isSaved: true }

                    // update orderItem.id to match the created one (generated from Backend)
                    const matchedNewOrderLine = lastUpdatedOrderLines?.find(
                        (ol) => ol.tempOrderLineID === o.id,
                    )
                    if (matchedNewOrderLine)
                        newOrderItem.id = matchedNewOrderLine.orderLineID!

                    return newOrderItem
                }),
                taxResult: {
                    ...state.taxResult,
                    taxLines: state.taxResult.taxLines?.map((t) =>
                        updateOrderLineIdForTaxLines(t),
                    ),
                },
            }

            function updateOrderLineIdForTaxLines(t: LineModelExtend) {
                // update orderItem.id to match the created one (generated from Backend)
                const matchedNewOrderLine = lastUpdatedOrderLines?.find(
                    (ol) => ol.tempOrderLineID === t.orderItemId,
                )
                if (matchedNewOrderLine)
                    t.orderItemId = matchedNewOrderLine.orderLineID!
                return t
            }
        }
        case 'SELL_PREVIEW_BILL_OFFLINE': {
            return {
                ...state,
                editingOrder: undefined,
                orderServiceChargeSetting: defaultServiceChargeSetting(
                    state.defaultServiceChargeRate,
                ),
            }
        }
        case 'SELL_PROCESS_ORDER_FAILED': {
            return {
                ...state,
                sellProcessingState: SellProcessingState.Failed,
                sellProcessingMessage: action.payload.message,
                sellProcessingAdditionValue: action.payload.additionValue,
            }
        }
        case 'SELL_PROCESS_SET_IDLE': {
            return {
                ...state,
                orderProcessingMode: OrderProcessingMode.NON_SELECTED,
                sellProcessingState: SellProcessingState.Idle,
                loadOrderState: SellProcessingState.Idle,
            }
        }
        case 'SELL_CLEAR_ORDER': {
            return {
                ...state,
                orderItems: [],
                orderNote: '',
                currentFulfilmentType: state.activeFulfillment[0],
                taxResult: { taxLines: [] },
                editingOrder: undefined,
                orderServiceChargeSetting: defaultServiceChargeSetting(
                    state.defaultServiceChargeRate,
                ),
                orderCustomerID: undefined,
                entireOrderDiscounts: [],
                excludeAutoDiscountIds: [],
                selectedItemInCart: undefined,
                entireOrderVoidComp: undefined,
                sellProcessingState: SellProcessingState.Idle,
                orderProcessingMode: OrderProcessingMode.NON_SELECTED,
                loadOrderState: SellProcessingState.Idle,
                selectedBrandID: '',
                orderSale: undefined,
                removedData: {},
                pickupDateTime: null,
                isAsap: null,
                hasAwaitingProcessTransaction: false,
            }
        }
        case 'SELL_SET_ORDER_PROCESSING_MODE': {
            return {
                ...state,
                orderProcessingMode: action.payload,
            }
        }
        case 'SELL_SET_CURRENT_FULFILMENT_TYPE': {
            return {
                ...state,
                currentFulfilmentType: action.payload,
            }
        }
        case 'SELL_LOAD_FULFILMENT_BEGIN': {
            return {
                ...state,
                fulfilmentLoading: true,
            }
        }
        case 'SELL_LOAD_FULFILMENT_SUCCESS': {
            const activeFulfillments =
                action.payload
                    ?.filter((e) => e.isActive)
                    .map((e) => e.orderFulfillmentTypeID!!)
                    ?.sort((a, b) => a - b) ?? []

            const isActiveCurrentFulfillments = activeFulfillments.includes(
                state.currentFulfilmentType,
            )
            return {
                ...state,
                currentFulfilmentType: isActiveCurrentFulfillments
                    ? state.currentFulfilmentType
                    : activeFulfillments[0],
                activeFulfillment: activeFulfillments,
                fulfilmentLoading: false,
            }
        }
        case 'SELL_LOAD_FULFILMENT_FAILED': {
            return {
                ...state,
                fulfilmentLoading: false,
            }
        }
        case 'SELL_LOAD_ORDER_BEGIN': {
            return {
                ...state,
                loadOrderState: SellProcessingState.Processing,
            }
        }
        case 'SELL_LOAD_ORDER_SUCCESS': {
            //if the sell processing state it do not run in correct sequency. it means there are some action interupt the loading data
            //such as clear while loading, So should not replace the order
            if (state.loadOrderState === SellProcessingState.Idle)
                return { ...state }
            const { orderChannelID } = state
            const {
                order: loadedOrder,
                orderItems,
                removedData,
                discounts,
            } = action.payload
            // If there's any extra stuffs to convert from OrderLookupContract --> OrderContract please make it done here!!
            const editingOrder = { ...loadedOrder }
            const orderSale = loadedOrder?.sale

            // discount
            const entireOrderDiscounts = buildEntireOrderDiscounts(
                loadedOrder.orderDiscounts ?? [],
                discounts,
            )

            const entireOrderVoidComp: VoidCompReasonModel | undefined =
                buildEntireVoidCompModel(loadedOrder)

            // All auto discounts which has been removed from this order before
            // -> Do not apply these discounts automatedly
            let excludeAutoDiscountIdsList: string[] = []

            // If this is reloading process -> copy excluded discounts from the existing
            if (state.editingOrder?.orderID === loadedOrder.orderID)
                excludeAutoDiscountIdsList = state.excludeAutoDiscountIds
            else {
                // All possible Auto Item discounts
                const possibleAutoItemDiscounts: DiscountContractWithCustom[] =
                    orderItems.reduce(
                        (acc: DiscountContractWithCustom[], curr) => {
                            if (curr.discounts && curr.discounts.length > 0) {
                                curr.discounts.forEach((e) => {
                                    if (
                                        !acc.find(
                                            (f) =>
                                                f.discountID === e.discountID,
                                        ) &&
                                        e.isAutomaticDiscount
                                    )
                                        acc.push(e)
                                })
                            }
                            return acc
                        },
                        [],
                    )

                const possibleAutoBogoItemDiscounts: BuyXGetYResult[] =
                    mapRequiredItemWithDiscount(
                        orderItems.filter((item) => !isPromotionItem(item)),
                        discounts.filter(
                            (i) =>
                                i.type === DiscountType.BuyXGetY &&
                                i.isAutomaticDiscount,
                        ),
                        orderItems.filter((item) => !isPromotionItem(item)),
                        editingOrder.orderFulfillmentTypeID!,
                        [],
                        orderChannelID,
                    )

                // All possible Auto Entire Order discounts
                const possibleAutoEntireOrderDiscounts: DiscountContractWithCustom[] =
                    autoDiscountByEntireOrderMapping(
                        orderItems,
                        discounts.filter(
                            (i) =>
                                i.level === DiscountLevel.EntireOrder &&
                                i.isAutomaticDiscount,
                        ),
                        editingOrder.orderFulfillmentTypeID!,
                        // No excluded discounts for getting all possible entire order discounts
                        [],
                        orderChannelID,
                    )

                // Excluded entire order discounts
                excludeAutoDiscountIdsList =
                    possibleAutoEntireOrderDiscounts
                        .flatMap((d) => d.discountID!)
                        .filter(
                            (i) =>
                                !entireOrderDiscounts.some(
                                    (ed) => ed.discountID === i,
                                ),
                        ) ?? []

                let temp: OrderItemViewModel[] = []

                // Excluded item discounts
                possibleAutoItemDiscounts.forEach((e) => {
                    if (e.appliesTo === DiscountAppliesTo.SelectedProducts) {
                        temp = orderItems.filter((f) =>
                            e.applyToIDs?.includes(f.item.itemId),
                        )
                    } else if (
                        e.appliesTo === DiscountAppliesTo.SelectedCategories
                    ) {
                        temp = orderItems.filter((f) =>
                            e.applyToIDs?.includes(f.item.categoryId),
                        )
                    }

                    if (
                        temp.filter((f) => f.discounts?.length === 0).length !==
                        0
                    ) {
                        excludeAutoDiscountIdsList.push(e.discountID!!)
                    }
                })

                // BOGO Discounts
                if (!isEmpty(possibleAutoBogoItemDiscounts)) {
                    const promotionItems = orderItems.filter((oi) =>
                        isPromotionItem(oi),
                    )
                    possibleAutoBogoItemDiscounts.forEach((e) => {
                        const bogoOrderItems = promotionItems.filter((i) =>
                            i.discounts?.some(
                                (d) => d.discountID === e.discount.discountID,
                            ),
                        )

                        // If all possible bogo items have not been applied -> Exclude
                        if (bogoOrderItems.length < e.additionalGetQty)
                            excludeAutoDiscountIdsList.push(
                                e.discount.discountID!,
                            )
                    })
                }
            }

            return {
                ...state,
                currentFulfilmentType: editingOrder.orderFulfillmentTypeID!,
                orderCustomerID: editingOrder.customerID!,
                editingOrder: editingOrder as any,
                orderNote: editingOrder.note ?? '',
                orderItems: [...orderItems],
                savedOrderItems: [...orderItems].filter((i) => i.isSaved),
                entireOrderVoidComp,
                entireOrderDiscounts,
                excludeAutoDiscountIds: excludeAutoDiscountIdsList,
                orderSale,
                removedData,
                pickupDateTime: loadedOrder.isAsap
                    ? dayjs().format()
                    : loadedOrder.pickUpDateTime,
                isAsap: loadedOrder.isAsap,
                deliveryAddress: {
                    customerAddressID:
                        loadedOrder.deliveryDetails?.customerAddressID,
                    addressName: loadedOrder.deliveryDetails?.addressName,
                    fullAddress: loadedOrder.deliveryDetails?.fullAddress,
                    deliveryNote: loadedOrder.deliveryDetails?.deliveryNote,
                },
                loadOrderState: SellProcessingState.Success,
                orderServiceChargeSetting: {
                    ...state.orderServiceChargeSetting,
                    currentServiceChageRate:
                        editingOrder.serviceChargeRate ?? 0,
                },
                hasAwaitingProcessTransaction:
                    loadedOrder.hasAwaitingProcessTransaction ?? false,
            }
        }
        case 'SELL_LOAD_ORDER_FAILED': {
            return { ...state, loadOrderState: SellProcessingState.Failed }
        }
        case 'APPLY_SELECT_DISCOUNT_ITEMS': {
            const items = action.payload.items as OrderItemViewModel[]
            let entireDiscounts = action.payload
                .entireDiscounts as DiscountContract[]

            const resetEntire = action.payload.resetEntire

            if (!resetEntire) {
                entireDiscounts = sortDiscountTypeOfDiscount([
                    ...state.entireOrderDiscounts,
                    ...entireDiscounts,
                ])
            }

            return {
                ...state,
                orderItems: state.orderItems.map((m) => {
                    const newItem = items.find((s) => s.id === m.id)
                    if (newItem) {
                        newItem.isSaved = false
                        return newItem
                    }
                    return m
                }),
                entireOrderDiscounts: entireDiscounts,
            }
        }
        case 'SELL_REMOVE_VOID_ORDER': {
            return {
                ...state,
                entireOrderVoidComp: undefined,
            }
        }
        case 'SELL_REMOVE_VOIDCOMP_ITEM': {
            const items = action.payload
            if (items.length === 0)
                return {
                    ...state,
                    entireOrderVoidComp: undefined,
                }

            return {
                ...state,
                orderItems: state.orderItems.map((m) => {
                    const updateItem = items.find((s) => s.id === m.id)
                    if (updateItem) {
                        return { ...updateItem, voidComp: undefined }
                    }
                    return m
                }),
            }
        }
        case 'SELL_ORDER_VOIDCOMP': {
            const voidComp = action.payload.voidComp
            const items = action.payload.items

            const newOrderItems = state.orderItems.flatMap((m) => {
                const voidCompItem = items.find((s) => s.id === m.id)
                if (voidCompItem) {
                    const item = {
                        ...voidCompItem,
                        status: voidCompItem.status,
                        voidComp: {
                            ...voidComp,
                            value:
                                voidComp?.type === VoidCompReasonType.Void
                                    ? // keep existing comp value
                                      m.voidComp?.value
                                    : voidComp?.value,
                        },
                    }
                    return splitToVoidOrderItems(item)
                }
                return [m]
            })

            const isVoidCompOrder = items.length === 0

            const entireOrderVoidComp = isVoidCompOrder
                ? {
                      entireOrderVoidComp: {
                          ...voidComp,
                          value:
                              voidComp?.type === VoidCompReasonType.Void
                                  ? // keep existing comp value
                                    state.entireOrderVoidComp?.value
                                  : voidComp?.value,
                      },
                  }
                : {}

            return {
                ...state,
                orderItems: newOrderItems,
                ...entireOrderVoidComp,
            }

            function splitToVoidOrderItems(orderItem: SelectedOrderItem) {
                const voidQty = orderItem.voidCompQuantity

                if (
                    !voidQty ||
                    voidQty === orderItem.amount ||
                    !orderItem.voidComp
                )
                    return [orderItem]

                const voidedItem = {
                    ...orderItem,
                    id: `${orderItem.id}-void-${Date.now()}`,
                    status: OrderLineStatus.New,
                    amount: voidQty,
                    isSaved: false,
                }
                const remainingItem = {
                    ...orderItem,
                    status: OrderLineStatus.New,
                    amount: orderItem.amount - voidQty,
                    voidComp: undefined,
                }

                return [voidedItem, remainingItem]
            }
        }

        case 'SELL_SEARCH_ITEM': {
            const searchString = action.payload as string

            return {
                ...state,
                searchString,
                previousSelected: {
                    categoryId: state.selectedCategoryId,
                    menuSectionId: state.selectedMenuSectionId,
                },
            }
        }

        case 'SET_CUSTOMER_DIALOG_OPEN_STATUS': {
            return {
                ...state,
                isCustomerDialogOpen: action.payload,
            }
        }
        case 'SET_GIFT_CARD_DIALOG_OPEN_STATUS': {
            return {
                ...state,
                isGiftCardDialogOpen: action.payload,
            }
        }
        case 'SET_VOID_DIALOG_OPEN_STATUS': {
            return {
                ...state,
                isVoidDialogOpen: action.payload,
            }
        }
        case 'SELL_MAKE_ANOTHER': {
            const { newItem, autoItemDiscounts, menus, priceList } =
                action.payload
            const {
                orderItems,
                waitingSentToKdsOrderLineIds,
                currentFulfilmentType,
                excludeAutoDiscountIds,
                orderChannelID,
            } = state

            const { newOrderItems, newAddedItem } = mergeCartItems(
                orderItems,
                newItem,
                waitingSentToKdsOrderLineIds,
                menus,
                priceList,
            )

            const itemsWithDiscounts = autoDiscountByItemsMapping({
                automaticDiscounts: autoItemDiscounts,
                checkingFulfillmentType: currentFulfilmentType,
                excludeAutoDiscountIds,
                orderChannel: orderChannelID,
            })(newOrderItems)

            const newMakedAnotherItem = itemsWithDiscounts.find(
                (i) => i.id === newAddedItem.id,
            )

            return {
                ...state,
                orderItems: itemsWithDiscounts,
                latestOrderItem: newMakedAnotherItem,
                selectedItemInCart: newMakedAnotherItem && {
                    type: SelectItemInCartType.item,
                    data: newMakedAnotherItem,
                },
            }
        }
        case 'SELL_VOID_ORDER_SUCCESS': {
            return {
                ...state,
                sellProcessingState: SellProcessingState.Voided,
            }
        }
        case 'SELL_VOID_ORDER_BEGIN': {
            return {
                ...state,
                sellProcessingState: SellProcessingState.Processing,
            }
        }
        case 'SELL_VOID_ORDER_FAILED': {
            return {
                ...state,
                sellProcessingState: SellProcessingState.Failed,
            }
        }
        case 'SELL_SELECT_CUSTOMER_ADDRESS': {
            return {
                ...state,
                selectCustomerAddressId: action.payload,
            }
        }
        case 'SELL_ASSIGN_CUSTOMER_TO_ORDER': {
            return {
                ...state,
                orderCustomerID: action.payload,
            }
        }
        case 'SET_DEFAULT_SERVICE_CHARGE_RATE': {
            return {
                ...state,
                defaultServiceChargeRate: action.payload,
                orderServiceChargeSetting:
                    !state.orderServiceChargeSetting.useManualServiceCharge &&
                    !state.orderServiceChargeSetting.disableServiceCharge
                        ? // if user's not apply manual service charge and the service charge is not disabled, we set the current order service charge to default rate
                          defaultServiceChargeSetting(action.payload)
                        : // keep using old setting
                          state.orderServiceChargeSetting,
            }
        }

        case 'REMOVE_DISCOUNT_INAPPLICABLE': {
            // Validate entire discount inapplicables (3 Step)
            // Step 1: get entire discount inapplicable for removing current entireOrderDiscount (state)
            const {
                orderItems: currentOrderItems,
                currentFulfilmentType,
                orderChannelID,
            } = state
            let updatedEntireOrderDiscount = state.entireOrderDiscounts

            const nonDepositAndCreditProducts = currentOrderItems.filter(
                (i) => !Boolean(i.item.baseDepositAndCreditItemId),
            )

            const entireOrderDiscountInApplicables =
                getEntireDiscountInApplicables(
                    nonDepositAndCreditProducts,
                    updatedEntireOrderDiscount,
                    currentFulfilmentType,
                    orderChannelID,
                )

            // Step 2: remove entire order discount inapplicable
            if (entireOrderDiscountInApplicables.length > 0)
                updatedEntireOrderDiscount = updatedEntireOrderDiscount.filter(
                    (en) =>
                        entireOrderDiscountInApplicables.some(
                            (app) => app.discountID !== en.discountID,
                        ),
                )

            // Step 3: alert discount inaplicable (discount applicable not empty)
            const alertDiscountInapplicable =
                entireOrderDiscountInApplicables.length !== 0

            return {
                ...state,
                entireOrderDiscounts: updatedEntireOrderDiscount,
                openAlertDiscountInApplicable: alertDiscountInapplicable,
            }
        }
        case 'ALERT_DISCOUNT_INAPPLICABLE': {
            return {
                ...state,
                openAlertDiscountInApplicable: action.payload,
            }
        }
        case 'SELL_UPDATE_ORDER_PAYMENTS': {
            return {
                ...state,
                editingOrder: {
                    ...state.editingOrder,
                    status:
                        action.payload.orderStatus ??
                        state.editingOrder?.status,
                    orderPaymentStatus: action.payload.orderPaymentStatus,
                },
                orderSale: {
                    ...state.orderSale,
                    payments: action.payload.payments,
                },
            }
        }
        case 'UPDATE_ORDER_AND_ORDER_LINES_STATUS': {
            const updatedOrder = action.payload
            const updatedOrderLines = updatedOrder.orderLines ?? []
            const orderItems = state.orderItems.map<OrderItemViewModel>((a) => {
                const updatedItem = updatedOrderLines.find(
                    (l) => l.orderLineID === a.id,
                )
                if (updatedItem)
                    return {
                        ...a,
                        status: updatedItem.status ?? OrderLineStatus.New,
                        kdsStatus:
                            updatedItem.kdsStatus ?? OrderLineKdsStatus.Unknown,
                        kitchenPrintStatus:
                            updatedItem.kitchenPrintStatus ??
                            KitchenPrintStatus.NotPrintOrUndefined,
                        voidComp:
                            updatedItem.status &&
                            updatedItem.status === OrderLineStatus.Voided
                                ? {
                                      voidCompReasonID:
                                          updatedItem.voidCompReasonID!,
                                      name: updatedItem.voidCompReason!,
                                      type: VoidCompReasonType.Void!,
                                      note: updatedItem.voidCompNotes!,
                                      value: 0,
                                  }
                                : a.voidComp,
                    }
                return a
            })

            return {
                ...state,
                editingOrder: {
                    ...state.editingOrder,
                    status: updatedOrder.status ?? OrderStatus.New,
                    fulfillmentStatus:
                        updatedOrder.fulfillmentStatus ??
                        OrderFulfillmentStatus.Unfulfilled,
                    isSentToKDS: updatedOrder.isSentToKDS ?? false,
                },
                orderItems,
            }
        }
        case 'UPDATE_TRANSACTION_PROCESS_STATUS': {
            return {
                ...state,
                hasAwaitingProcessTransaction: action.payload,
            }
        }
        case 'CHECK_ITEM_VIEW_ERROR': {
            const errorChecker = action.payload.errorCheck
            const orderItems = state.orderItems.map((oi) => {
                return errorChecker.validateOrderItemsView(oi)
            })
            return {
                ...state,
                orderItems,
            }
        }
        case 'UPDATE_ITEM_KDS_STATUS': {
            const orderLines = action.payload.orderLines
            const orderItems = state.orderItems.map((oi) => {
                const updatedOrderLine = orderLines.find(
                    (ol) => ol.orderLineID === oi.id,
                )
                if (!updatedOrderLine) return oi
                return {
                    ...oi,
                    kdsStatus: updatedOrderLine.kdsStatus ?? oi.kdsStatus,
                    inventoryStatus:
                        updatedOrderLine.inventoryStatus ?? oi.inventoryStatus,
                    kitchenPrintStatus:
                        updatedOrderLine.kitchenPrintStatus ??
                        oi.kitchenPrintStatus,
                    status: updatedOrderLine.status ?? oi.status,
                }
            })

            let newSelectedItemInCart = state.selectedItemInCart
            const matchedUpdateLine = orderLines.find(
                (ol) => ol.orderLineID === newSelectedItemInCart?.data?.id,
            )
            if (newSelectedItemInCart && matchedUpdateLine) {
                newSelectedItemInCart = {
                    ...newSelectedItemInCart,
                    data: {
                        ...newSelectedItemInCart.data,
                        kdsStatus: matchedUpdateLine.kdsStatus,
                    },
                }
            }

            return {
                ...state,
                orderItems,
                selectedItemInCart: newSelectedItemInCart,
            }
        }
        case 'UPDATE_ORDER_STATUS': {
            const order = action.payload
            const editingOrder = state.editingOrder
            if (isNil(editingOrder) || order.orderID !== editingOrder.orderID)
                return state
            return {
                ...state,
                editingOrder: {
                    ...editingOrder,
                    status: order.status ?? editingOrder.status,
                    fulfillmentStatus:
                        order.fulfillmentStatus ??
                        editingOrder.fulfillmentStatus,
                    orderPaymentStatus:
                        order.orderPaymentStatus ??
                        editingOrder.orderPaymentStatus,
                },
            }
        }
        case 'ADD_ORDER_LINE_SENDING': {
            const sentOrderLineIds = action.payload
            const sendingLines = uniq([
                ...sentOrderLineIds,
                ...state.waitingSentToKdsOrderLineIds,
            ])
            return {
                ...state,
                waitingSentToKdsOrderLineIds: sendingLines,
            }
        }
        case 'REMOVE_NOTIFIED_ORDER_LINE_IDS': {
            // Remove all notified order line ids from state
            const notifiedOrderLineIds = action.payload
            return {
                ...state,
                waitingSentToKdsOrderLineIds: difference(
                    state.waitingSentToKdsOrderLineIds,
                    notifiedOrderLineIds,
                ),
            }
        }
        case 'CLEAR_ORDER_LINE_SENDING': {
            return {
                ...state,
                waitingSentToKdsOrderLineIds: [],
            }
        }
        case 'SET_IS_CHECKING_STOCK_AVAILABILITY': {
            return {
                ...state,
                isCheckingOrderStockAvailability: action.payload,
            }
        }
        case 'UPDATE_KITCHEN_PRINT_STATUS': {
            const payload = action.payload

            return {
                ...state,
                orderItems: state.orderItems.map((curOi) => {
                    if (payload.orderLineIds.includes(curOi.id))
                        return {
                            ...curOi,
                            kitchenPrintStatus: payload.newStatus,
                        }
                    else return curOi
                }),
            }
        }
        case 'QS_RESET_CONTEXT':
            return initialState
        default:
            return state
    }
}

/**
 * apply auto discount to order or remove discount when no item left
 * @param orderItems
 * @param appliedDiscounts discount that already applied to order
 * @param availableDiscounts all available order discounts
 * @param excludeAutoDiscountIds the auto discount that user manually remove
 * @param currentFulfilmentType
 * @param currentOrderChannelID
 * @returns
 */
const prepareEntireOrderDiscounts = (
    orderItems: OrderItemViewModel[],
    availableDiscounts: DiscountContract[],
    currentFulfilmentType: OrderFulfilmentType,
    excludeAutoDiscountIds: string[] = [],
    currentOrderChannelID: number,
): DiscountContractWithCustom[] => {
    /**
     * for now apply ONLY FixedAmount type and Percentage type
     */
    const autoDiscounts =
        orderItems.length === 0
            ? []
            : availableDiscounts
                  .filter((f) => f.isAutomaticDiscount)
                  .filter(
                      (f) =>
                          f.type &&
                          (SIMPLE_DISCOUNT_TYPE.includes(f.type) ||
                              FUTURE_DISCOUNT_TYPE.includes(f.type)),
                  )

    return autoDiscountByEntireOrderMapping(
        orderItems,
        autoDiscounts,
        currentFulfilmentType,
        excludeAutoDiscountIds,
        currentOrderChannelID,
    )
}
